import { CalendarDate, today, toZoned } from "@internationalized/date"
import {
  useMutation,
  useQueryClient,
  useSuspenseQuery,
} from "@tanstack/react-query"
import { createFileRoute } from "@tanstack/react-router"
import { InventoryTreeRateMiniStatus } from "ipctg-inflow-types/InFlowEnums"
import { InventoryTreeRateMini } from "ipctg-inflow-types/InFlowReserveAPI"
import { useCallback, useState } from "react"
import { GridList, Key } from "react-aria-components"
import { useTranslation } from "react-i18next"

import Heading from "@/globalComponents/atoms/Heading"
import AccessKeyCallout from "@/routes/qr/$qrLookupID/reserve.$epoch/-components/AccessKeyCallout"
import { formatDateForDisplay, getLocale } from "@/utilities/etc"
import { ReserveDateInPastError } from "@/utilities/expectedErrors"
import {
  RESERVE_BASE_KEY,
  RESERVE_RATES_BASE_KEY,
} from "@/utilities/hooks/queries/keys"
import {
  applyAccessCodeMutationOptions,
  ratesQueryOptions,
  selectReserveRate,
} from "@/utilities/hooks/queries/reserve"
import { themeQueryOptions } from "@/utilities/hooks/queries/shared"
import { useErrorModal } from "@/utilities/hooks/useErrorModal"
import { useTheme } from "@/utilities/hooks/useTheme"
import { log } from "@/utilities/logging"

import { MembershipModal } from "./-components/MembershipModal"
import {
  RateUnavailableModal,
  RateUnavailableReason,
} from "./-components/RateUnavailableModal"
import { RateWarningModal } from "./-components/RateWarningModal"
import ReservationRateCard from "./-components/ReservationRateCard"
import styles from "./index.module.css"

export const Route = createFileRoute(
  "/qr/$qrLookupID/reserve/$epoch/calendars/$calendarID/dates/$year/$month/$day/rates/"
)({
  staticData: {
    pageName: "Rates",
  },
  beforeLoad: async ({ params, context }) => {
    const { qrLookupID, year, month, day } = params
    const theme = await context.queryClient.ensureQueryData(
      themeQueryOptions({ qrLookupID })
    )
    const timezoneLocation = theme!.locationInformation.timeZone
    const calendarDate = new CalendarDate(
      parseInt(year, 10),
      parseInt(month, 10),
      parseInt(day, 10)
    )
    if (calendarDate.compare(today(timezoneLocation)) < 0) {
      throw new ReserveDateInPastError()
    }
    const startDate = toZoned(calendarDate, timezoneLocation)
    return { startDate }
  },
  component: RouteComponent,
})

function RouteComponent() {
  const navigate = Route.useNavigate()
  const { calendarID, year, month, day } = Route.useParams()
  const { startDate } = Route.useRouteContext()
  const { t, i18n } = useTranslation("reserve")
  const { scannedArea } = useTheme()
  const locationID = scannedArea!.theme.locationInformation.locationID
  const queryClient = useQueryClient()
  const { setError } = useErrorModal()
  const { data: rates } = useSuspenseQuery(
    ratesQueryOptions({
      locationID,
      bookingCalendarID: calendarID,
      year,
      month,
      day,
    })
  )
  const [membershipModalName, setMembershipModalName] = useState<string | null>(
    null
  )
  const [unavailableReason, setUnavailableReason] =
    useState<RateUnavailableReason | null>(null)
  const [pendingRate, setPendingRate] = useState<InventoryTreeRateMini | null>(
    null
  )

  const selectRateMutation = useMutation({
    mutationFn: (rateID: string) => selectReserveRate(rateID),
    onSuccess: () =>
      navigate({
        to: "/qr/$qrLookupID/reserve/$epoch/calendars/$calendarID/dates/$year/$month/$day/vehicles",
      }),
    onError: (err) => {
      queryClient.invalidateQueries({
        queryKey: [RESERVE_BASE_KEY, RESERVE_RATES_BASE_KEY],
      })
      log({
        level: "warn",
        flow: "reserve",
        scope: "fetchResponse.selectRate",
        message: "Failed to select rate",
        meta: { error: err },
      })
      setError({ error: err instanceof Error ? err : new Error(String(err)) })
    },
    onSettled: () => setPendingRate(null),
  })

  const handleOpenMembershipModal = useCallback(
    (membershipName: string | null) => setMembershipModalName(membershipName),
    []
  )

  return (
    <>
      <Heading level={1} className="center">
        {formatDateForDisplay({
          isoDateString: startDate.toAbsoluteString(),
          locale: i18n.resolvedLanguage || getLocale(),
          timeZone: startDate.timeZone,
        })}
      </Heading>
      <AccessKeyCallout
        mutationOptions={applyAccessCodeMutationOptions(queryClient, locationID)}
      />
      <GridList
        aria-label={t("rates.heading")}
        className={styles.ratesContainer}
      >
        {rates.map((rate) => (
          <ReservationRateCard
            key={rate.inventoryTreeRateMiniID}
            rate={rate}
            id={rate.inventoryTreeRateMiniID}
            textValue={rate.rateName}
            startDate={startDate}
            locale={i18n.resolvedLanguage || getLocale()}
            onOpenMembershipModal={handleOpenMembershipModal}
            onAction={() => handleRateAction(rate.inventoryTreeRateMiniID)}
            isPending={
              selectRateMutation.isPending &&
              selectRateMutation.variables === rate.inventoryTreeRateMiniID
            }
            isDisabled={
              selectRateMutation.isPending &&
              selectRateMutation.variables !== rate.inventoryTreeRateMiniID
            }
          />
        ))}
      </GridList>
      <RateUnavailableModal
        reason={unavailableReason}
        onClose={() => setUnavailableReason(null)}
      />
      <MembershipModal
        membershipName={membershipModalName}
        onClose={() => setMembershipModalName(null)}
      />
      <RateWarningModal
        pendingRate={pendingRate}
        isPending={selectRateMutation.isPending}
        onConfirm={() =>
          pendingRate &&
          selectRateMutation.mutate(pendingRate.inventoryTreeRateMiniID)
        }
        onClose={() => setPendingRate(null)}
      />
    </>
  )

  function handleRateAction(key: Key) {
    const rate = rates.find((r) => r.inventoryTreeRateMiniID === key)
    if (!rate) return
    if (rate.rateStatus === InventoryTreeRateMiniStatus.Ended) {
      setUnavailableReason("ended")
      return
    }
    if (rate.rateStatus === InventoryTreeRateMiniStatus.PrivilegeExhausted) {
      setUnavailableReason("privilegeExhausted")
      return
    }
    if (rate.rateStatus !== InventoryTreeRateMiniStatus.Available) {
      setUnavailableReason("soldOut")
      return
    }
    if (rate.warningMessage) {
      setPendingRate(rate)
      return
    }
    selectRateMutation.mutate(rate.inventoryTreeRateMiniID)
  }
}
